import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Card,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Menu,
  Tooltip,
  Avatar,
  Link,
  Collapse,
  GlobalStyles,
  ToggleButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useClinicFeatures } from '../../Utils/useClinicFeatures';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { withReportNavigationState } from '../../Utils/reportNavigation';

import { AppDispatch } from '../../store/store';
import filtersMeta from '../../../app/src/routes/WorkList/filtersMeta';
import { getAllReportByStudyInstanceApi } from '../../rtk-apis/reports/getAllReportByStudyInstance';
import { getStudyMarkingsApi } from '../../rtk-apis/reports/getStudyMarkingsApi';
import { addStudyMarking } from '../../rtk-apis/reports/addStudyMarkingApi';
import { removeStudyMarking } from '../../rtk-apis/reports/removeStudyMarkingApi';
import { getStudyTopicsApi } from '../../rtk-apis/reports/getStudyTopicsApi';
import Success from '../../components/Success';
import SelectTopicModal from '../../components/SelectTopicModal';
import PatientAttachmentsModal from '../patients/components/patientAttachments/PatientAttachmentsModal';
import { STATUS_LABELS } from '../express/DistributionPage';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Cardiac Study List — MUI rewrite (migration phase 2). New component; the old
   CardiacStudyList.tsx + cardiacStudyList.css stay in place until the phase-7
   cleanup sweep. All data/logic is identical to the original; only the
   presentation moved to MUI + the shared cardiacTheme.
   ════════════════════════════════════════════════════════════════════════ */

type StatusKey = 'Draft' | 'Preliminary' | 'Finalized' | string;

interface Study {
  studyInstanceUid: string;
  patientName?: string;
  mrn?: string;
  date?: string;
  time?: string;
  modalities?: string;
  instances?: number;
  reportId?: string;
  reportStatus?: string;
  reportType?: string;
  [k: string]: any;
}

interface Props {
  studies: Study[];
  numOfStudies: number;
  filterValues: any;
  setFilterValues: (v: any) => void;
  defaultSortValues: any;
  templateList: any;
  distributionCounts: Record<string, any>;
  role: string | null;
  pageNumber: number;
  resultsPerPage: number;
  onPageNumberChange: (n: number) => void;
  onResultsPerPageChange: (n: number) => void;
  isLoading?: boolean;
}

const STATUS_FILTERS = ['', 'Draft', 'Preliminary', 'Finalized'];
const STATUS_FILTER_LABEL: Record<string, string> = {
  '': 'All',
  Draft: 'Draft',
  Preliminary: 'Preliminary',
  Finalized: 'Finalized',
};
const RESULTS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// status → theme colour
const statusColor = (s?: StatusKey): string => {
  const v = (s || '').toLowerCase();
  if (v === 'finalized') return T.green;
  if (v === 'preliminary') return T.sky;
  if (v === 'draft') return T.amber;
  return T.ink3;
};

const initialsOf = (name?: string) => {
  if (!name) return '—';
  const clean = name.replace(/\^/g, ' ').replace(/,/g, ' ').trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const prettyName = (name?: string) => (name ? name.replace(/\^/g, ', ') : '(Unknown patient)');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The studies list's date rendering, exported so the reports list prints the same thing.
 *
 * Two stored forms, because the two lists read different fields: DICOM `YYYYMMDD` for a
 * study, and `DD/MM/YYYY` for a report's own creation date (`formatDate` in
 * reports/helper.ts). Anything else is printed untouched rather than guessed at.
 */
export const fmtDate = (date?: string) => {
  if (!date) return '';
  const dicom = String(date).match(/^(\d{4})(\d{2})(\d{2})/);
  if (dicom) {
    return `${+dicom[3]} ${MONTHS[+dicom[2] - 1]} ${dicom[1]}`;
  }
  const slashed = String(date).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashed) {
    return `${+slashed[1]} ${MONTHS[+slashed[2] - 1]} ${slashed[3]}`;
  }
  return String(date);
};
export const fmtTime = (time?: string) => {
  if (!time) return '';
  const m = String(time).match(/^(\d{2})(\d{2})/);
  if (!m) return '';
  let h = +m[1];
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
};

const optionsFor = (name: string) =>
  filtersMeta.find((f: any) => f.name === name)?.inputProps?.options || [];

// Column visibility: hide secondary columns as the viewport narrows (mirrors the
// old CSS breakpoints — keep identity/status/actions, drop the middle metadata).
const colSx = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '2px',
};

const CardiacStudyListV2: React.FC<Props> = ({
  studies,
  numOfStudies,
  filterValues,
  setFilterValues,
  defaultSortValues,
  templateList,
  distributionCounts,
  role,
  pageNumber,
  resultsPerPage,
  onPageNumberChange,
  onResultsPerPageChange,
  isLoading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // Per-clinic feature control: which of the viewer / measurement entry points
  // this clinic is allowed to see.
  const { dicomViewerEnabled, measurementEnabled } = useClinicFeatures();

  const [reportInfo, setReportInfo] = useState<Record<string, any>>({});
  const [markings, setMarkings] = useState<{
    alertList: any[];
    studyResearchList: any[];
    afList: string[];
  }>({
    alertList: [],
    studyResearchList: [],
    afList: [],
  });
  const [topics, setTopics] = useState<{ value: string; label: string }[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [genMenu, setGenMenu] = useState<{ anchor: HTMLElement; study: Study } | null>(null);
  const [toast, setToast] = useState('');
  const [topicModalFor, setTopicModalFor] = useState<string | null>(null);
  // The success modal has no timer of its own, so it sat on screen until the
  // user dismissed it. These are confirmations, not decisions — clear them.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2500);
    return () => clearTimeout(t);
  }, [toast]);
  const [attachFor, setAttachFor] = useState<string | null>(null);

  const modalityOpts = useMemo(() => optionsFor('modalities'), []);
  const typeOpts = useMemo(() => optionsFor('reportType'), []);

  // Stable primitive keys so the effect fires only when the actual ids/filters
  // change — NOT on every parent re-render (the parent re-slices studies each
  // render, so depending on the array ref caused an infinite fetch loop).
  const uidsKey = useMemo(
    () => studies.map(s => s.studyInstanceUid).filter(Boolean).join(','),
    [studies]
  );
  const statusKey = (filterValues.reportStatus || []).join(',');
  const typeKey = (filterValues.reportType || []).join(',');

  // ── report status/type/assignee for the visible studies ──────────────────
  useEffect(() => {
    const ids = uidsKey ? uidsKey.split(',') : [];
    if (!ids.length) {
      setReportInfo({});
      return;
    }
    dispatch(
      getAllReportByStudyInstanceApi({
        studyInstanceUIDs: ids,
        reportStatus: statusKey ? statusKey.split(',') : [],
        reportType: typeKey ? typeKey.split(',') : [],
      })
    )
      .unwrap()
      .then((res: any) => {
        const map: Record<string, any> = {};
        (res?.data || []).forEach((r: any) => {
          map[r.studyInstanceUID] = r;
        });
        setReportInfo(map);
      })
      .catch(() => setReportInfo({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, uidsKey, statusKey, typeKey]);

  // ── critical / research markings ─────────────────────────────────────────
  const loadMarkings = () => {
    dispatch(getStudyMarkingsApi())
      .unwrap()
      .then((res: any) => {
        if (res?.data) setMarkings({ alertList: res.data.alertList || [], studyResearchList: res.data.studyResearchList || [], afList: res.data.afList || [] });
      })
      .catch(() => {});
  };
  useEffect(() => { loadMarkings(); }, [dispatch]);

  useEffect(() => {
    dispatch(getStudyTopicsApi())
      .unwrap()
      .then((res: any) => {
        const list = res?.data?.[0]?.topicList ?? [];
        setTopics(list.map((tp: string) => ({ value: tp, label: tp })));
      })
      .catch(() => {});
  }, [dispatch]);

  const isCritical = (uid: string) =>
    markings.alertList?.some((x: any) => (typeof x === 'string' ? x === uid : x?.studyInstanceUID === uid));
  const isResearch = (uid: string) =>
    markings.studyResearchList?.some((x: any) => typeof x === 'object' && x?.studyInstanceUID === uid);
  const isAF = (uid: string) => markings.afList?.includes(uid);

  // ── filter mutation (mirrors the shape WorkList/InputGroup expects) ───────
  const applyFilter = (patch: any) => {
    setFilterValues({ ...filterValues, ...defaultSortValues, pageNumber: 1, ...patch });
  };
  const onSearch = (patientName: string) => applyFilter({ patientName });
  const onMrn = (mrn: string) => applyFilter({ mrn });
  const onStatus = (s: string) => applyFilter({ reportStatus: s ? [s] : [] });
  // Multi-select: toggle a status in/out of the array (Draft + Finalized together, etc.).
  const onToggleStatus = (s: string) => {
    const cur: string[] = filterValues.reportStatus || [];
    const next = cur.includes(s) ? cur.filter((x: string) => x !== s) : [...cur, s];
    applyFilter({ reportStatus: next });
  };
  const onModality = (m: string) => {
    const cur = filterValues.modalities?.[0] || '';
    applyFilter({ modalities: cur === m ? [] : [m] });
  };
  const onType = (val: string) => applyFilter({ reportType: val ? [val] : [] });
  const onDate = (key: 'startDate' | 'endDate', val: string) =>
    applyFilter({ studyDate: { ...filterValues.studyDate, [key]: val ? val.replace(/-/g, '') : null } });
  const onFlags = (val: string) => applyFilter({ studyMarkings: val });
  const onTeaching = (val: string) => applyFilter({ teachingFile: val });

  const isFiltering =
    !!filterValues.patientName ||
    !!filterValues.mrn ||
    (filterValues.modalities?.length || 0) > 0 ||
    (filterValues.reportStatus?.length || 0) > 0 ||
    (filterValues.reportType?.length || 0) > 0 ||
    !!filterValues.studyMarkings ||
    !!filterValues.teachingFile ||
    !!filterValues.studyDate?.startDate ||
    !!filterValues.studyDate?.endDate;

  const clearAll = () => {
    setFilterValues({
      patientName: '', mrn: '', modalities: [], reportStatus: [], reportType: [],
      studyDate: { startDate: null, endDate: null }, sortBy: '', sortDirection: 'none',
      pageNumber: 1, resultsPerPage, datasources: '', configUrl: null, studyMarkings: '', teachingFile: '',
    });
    setMoreOpen(false);
  };

  // ── study markings toggles ────────────────────────────────────────────────
  // Every marking endpoint returns all three lists, so apply the whole response
  // rather than patching one list — otherwise toggling critical would blank the
  // AF marks held in the same document.
  const applyMarkings = (res: any) =>
    setMarkings({
      alertList: res?.data?.alertList || [],
      studyResearchList: res?.data?.studyResearchList || [],
      afList: res?.data?.afList || [],
    });

  const toggleCritical = async (uid: string) => {
    const wasCritical = isCritical(uid);
    const fn = wasCritical ? removeStudyMarking : addStudyMarking;
    try {
      const res: any = await dispatch(fn({ studyResearchList: [], alertList: [uid] } as any)).unwrap();
      applyMarkings(res);
      // Same rule as AF (client feedback 18 Aug, 22 Sep): setting the mark already shows
      // itself via the flag icon/badge, so only the removal needs a confirming toast.
      if (wasCritical) {
        setToast('Removed from alert list');
      }
    } catch { /* noop */ }
  };
  const toggleResearch = async (uid: string) => {
    if (isResearch(uid)) {
      const entry = markings.studyResearchList.find((x: any) => x?.studyInstanceUID === uid);
      try {
        const res: any = await dispatch(removeStudyMarking({ studyResearchList: [entry], alertList: [] } as any)).unwrap();
        applyMarkings(res);
        setToast('Removed from research list');
      } catch { /* noop */ }
    } else {
      setTopicModalFor(uid);
    }
  };
  const toggleAF = async (uid: string) => {
    const wasAF = isAF(uid);
    const fn = wasAF ? removeStudyMarking : addStudyMarking;
    try {
      const res: any = await dispatch(
        fn({ studyResearchList: [], alertList: [], afList: [uid] } as any)
      ).unwrap();
      applyMarkings(res);
      // Only the removal is confirmed. Setting the mark already shows itself —
      // the badge appears beside the patient name and the button lights up — so
      // a modal on top of that was pure interruption (client feedback 18 Aug).
      if (wasAF) {
        setToast('AF mark removed');
      }
    } catch { /* noop */ }
  };

  // ── navigation ────────────────────────────────────────────────────────────
  const openStudy = (s: Study) => {
    const info = reportInfo[s.studyInstanceUid] || {};
    navigate(`/express/${s.studyInstanceUid}`, {
      // Report metadata is loaded separately from the DICOM study list. Pass
      // the merged result so Express does not mistake an existing report for
      // a study that still needs one generated.
      state: withReportNavigationState(s, info),
    });
  };
  const openViewer = (uid: string) => navigate(`/viewer?StudyInstanceUIDs=${uid}`);
  const openMeasure = (uid: string) => navigate(`/measurement?StudyInstanceUIDs=${uid}`);
  const openReport = (reportId: string) => navigate(`/patientReports/${reportId}`);
  const openDistribution = (s: Study, info: any) =>
    navigate(`/distribution/${s.studyInstanceUid}`, {
      state: {
        reportId: info?.reportId || s.reportId,
        studyData: { ...s, name: s.patientName, patientId: s.mrn ?? s.patientId, studyDate: s.date, studyTime: s.time },
      },
    });

  const canGenerate = !['Medical Receptionist', 'Practice Manager', 'Admin'].includes(role || '');

  const onPickTemplate = (s: Study, templateName: string) => {
    const tpl = templateList?.data?.find((t: any) => t?.templateName === templateName);
    if (!tpl) return;
    navigate(`/express/${s.studyInstanceUid}`, {
      state: { ...s, defaultTemp: { item: tpl, hospitalHeader: templateList?.hospitalHeader, hospitalLogo: templateList?.hospitalLogo } },
    });
  };

  const totalPages = Math.max(1, Math.ceil((numOfStudies > 100 ? 100 : numOfStudies) / resultsPerPage));
  const countLabel = numOfStudies > 100 ? '100+' : numOfStudies;

  // Column order (matches the Reports list): Name · Assigned · Date · Type ·
  // Distribution · Status · actions. Visibility per breakpoint — track counts
  // below MUST match the number of visible grid items or the columns misalign:
  //   xs/sm (<900): Name, Status, actions                       → 3 tracks
  //   md/lg (900–1536): + Date + Type                           → 5 tracks
  //   xl (≥1536): + Assigned + Distribution                     → 7 tracks
  const dateColDisplay = { xs: 'none', md: 'flex' };
  const typeColDisplay = { xs: 'none', md: 'flex' };
  const secondaryColDisplay = { xs: 'none', xl: 'flex' };
  // Actions column is a FIXED width (not auto) so the header grid and each row grid
  // resolve their fr tracks identically — otherwise the empty header spacer (0px) vs
  // the row's action buttons (~330px) make the fr columns differ and labels drift.
  const ACTIONS_W = '360px';
  const gridCols = {
    xs: `1fr 120px ${ACTIONS_W}`,
    md: `minmax(180px,2fr) 0.9fr 1fr 1.1fr ${ACTIONS_W}`,
    xl: `minmax(210px,2.1fr) 1.1fr 0.9fr 1fr 1.1fr 1fr ${ACTIONS_W}`,
  };
  // Shared cell for the thin column-header band above the list.
  const HeadCell: React.FC<{ children?: React.ReactNode; display?: any }> = ({ children, display }) => (
    <Typography
      sx={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: T.ink3, whiteSpace: 'nowrap', display: display ?? 'flex', alignItems: 'center',
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Box
      className="csl"
      sx={{
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        color: T.ink,
        px: '30px',
        pb: '8px',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      {/* Carry the ancestor layout-hardening (was in cardiacStudyList.css) so the
          page never scrolls sideways. Safe: profile dropdown is portaled to body. */}
      <GlobalStyles
        styles={{
          '.cw-listpage': { overflowX: 'hidden', maxWidth: '100vw' },
          '.cw-listpage .layoutGrid, .cw-listpage .page, .cw-listpage .page__body': {
            overflowX: 'hidden',
            minWidth: 0,
          },
          '.cw-worklist__aurora': { maxWidth: '100%' },
          '.cw-listpage ::-webkit-scrollbar': { width: '10px', height: '10px' },
          '.cw-listpage ::-webkit-scrollbar-thumb': {
            background: T.lineStrong,
            borderRadius: '99px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '.cw-listpage ::-webkit-scrollbar-thumb:hover': { background: T.teal, backgroundClip: 'padding-box' },
        }}
      />

      {/* ─── command bar ─────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={2.5}
        sx={{ py: '22px', px: '4px' }}
      >
        {/* Title + count on one baseline row — no eyebrow (the top nav already
            says "Studies"); frees the vertical band for the column header. */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            component="h1"
            sx={{
              m: 0, fontSize: 30, fontWeight: 700, lineHeight: 1.05, letterSpacing: '-0.01em',
              background: 'linear-gradient(92deg, #fff 0%, #bfe9f5 55%, #7fe3d4 100%)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            Study List
          </Typography>
          <Typography sx={{ fontSize: 13, color: T.ink3 }}>
            <Box component="b" sx={{ color: T.teal, fontSize: 15, fontWeight: 800 }}>{countLabel}</Box> studies
          </Typography>
        </Box>

        <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" gap={1.5}>
          <TextField
            size="small"
            placeholder="Search patient name…"
            defaultValue={(filterValues.patientName || '').replace(/\^/g, ' ')}
            onKeyDown={e => { if (e.key === 'Enter') onSearch((e.target as HTMLInputElement).value); }}
            onBlur={e => { if (e.target.value !== (filterValues.patientName || '').replace(/\^/g, ' ')) onSearch(e.target.value); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: T.ink3 }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { borderRadius: '12px', background: T.surface2 } }}
          />
          <Button component={Link} href="/Guidelines_compressed.pdf" target="_blank" rel="noopener noreferrer" variant="outlined" sx={refLinkSx}>
            Quick Reference
          </Button>
          <Button component={Link} href="https://www.asecho.org/guidelines-search/" target="_blank" rel="noopener noreferrer" variant="outlined" sx={refLinkSx}>
            ASE Guidelines
          </Button>
        </Stack>
      </Stack>

      {/* ─── filter rail ─────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        gap={1.5}
        sx={{ px: '4px', pb: '14px', borderBottom: `1px solid ${T.line}`, mb: '6px' }}
      >
        {/* Multi-select status filter: "All" clears; Draft/Preliminary/Finalized toggle
            independently so several can be active at once (e.g. Draft + Finalized). */}
        <Box sx={{ display: 'inline-flex', background: T.surface, border: `1px solid ${T.line}`, borderRadius: '12px', p: '4px', gap: '3px' }}>
          {STATUS_FILTERS.map(s => {
            const selected = s === '' ? !(filterValues.reportStatus?.length) : (filterValues.reportStatus || []).includes(s);
            return (
              <ToggleButton
                key={s || 'all'}
                value={s || 'all'}
                selected={selected}
                onClick={() => (s === '' ? onStatus('') : onToggleStatus(s))}
                sx={{
                  border: 'none', borderRadius: '9px !important', color: T.ink2, fontSize: 13, fontWeight: 600,
                  px: '16px', py: '6px', textTransform: 'none',
                  '&:hover': { color: T.ink, background: 'transparent' },
                  '&.Mui-selected': {
                    background: T.surface2, color: `${s ? statusColor(s) : '#fff'} !important`,
                    boxShadow: `inset 0 0 0 1px ${T.lineStrong}`,
                    '&:hover': { background: T.surface2 },
                  },
                }}
              >
                {STATUS_FILTER_LABEL[s]}
              </ToggleButton>
            );
          })}
        </Box>

        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.875}>
          {modalityOpts.slice(0, 6).map((o: any) => {
            const active = filterValues.modalities?.[0] === o.value;
            return (
              <Chip
                key={o.value}
                label={o.label}
                clickable
                onClick={() => onModality(o.value)}
                sx={{
                  borderRadius: '999px', fontWeight: 600, fontSize: 12.5,
                  border: `1px solid ${active ? T.teal : T.line}`,
                  background: active ? 'rgba(45,212,191,0.14)' : 'rgba(22,38,59,0.45)',
                  color: active ? T.teal : T.ink2,
                  '&:hover': { background: active ? 'rgba(45,212,191,0.2)' : 'rgba(22,38,59,0.7)' },
                }}
              />
            );
          })}
        </Stack>

        <Button
          onClick={() => setMoreOpen(o => !o)}
          startIcon={<TuneIcon />}
          variant="outlined"
          sx={{
            ...refLinkSx,
            color: moreOpen ? T.teal : T.ink2,
            borderColor: moreOpen ? T.teal : T.line,
            background: moreOpen ? 'rgba(45,212,191,0.08)' : 'transparent',
          }}
        >
          More filters
        </Button>

        {isFiltering && (
          <Button
            onClick={clearAll}
            variant="outlined"
            sx={{ ...refLinkSx, ml: 'auto', color: T.red, borderColor: 'rgba(251,113,133,0.4)', '&:hover': { background: 'rgba(251,113,133,0.12)', borderColor: T.red, color: T.red } }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      <Collapse in={moreOpen} unmountOnExit>
        <Box
          sx={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px',
            p: '16px', mb: '8px', background: 'rgba(20,36,57,0.62)', border: `1px solid ${T.line}`, borderRadius: '14px',
          }}
        >
          <FilterField label="MRN">
            <TextField size="small" placeholder="Search by MRN" defaultValue={filterValues.mrn || ''} onBlur={e => onMrn(e.target.value)} sx={fieldInputSx} />
          </FilterField>
          <FilterField label="Study Start">
            <TextField size="small" type="date" InputLabelProps={{ shrink: true }} onChange={e => onDate('startDate', e.target.value)} sx={fieldInputSx} />
          </FilterField>
          <FilterField label="Study End">
            <TextField size="small" type="date" InputLabelProps={{ shrink: true }} onChange={e => onDate('endDate', e.target.value)} sx={fieldInputSx} />
          </FilterField>
          <FilterField label="Type">
            <Select size="small" value={filterValues.reportType?.[0] || ''} onChange={e => onType(e.target.value as string)} displayEmpty sx={fieldInputSx}>
              <MenuItem value="">All types</MenuItem>
              {typeOpts.map((o: any) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FilterField>
          <FilterField label="Flags">
            <Select size="small" value={filterValues.studyMarkings || ''} onChange={e => onFlags(e.target.value as string)} displayEmpty sx={fieldInputSx}>
              <MenuItem value="">No flag</MenuItem>
              <MenuItem value="critical">Critical studies</MenuItem>
              <MenuItem value="af">AF studies</MenuItem>
              <MenuItem value="research">Research studies</MenuItem>
            </Select>
          </FilterField>
          <FilterField label="Teaching file">
            <Select size="small" value={filterValues.teachingFile || ''} onChange={e => onTeaching(e.target.value as string)} displayEmpty sx={fieldInputSx}>
              <MenuItem value="">Any topic</MenuItem>
              {topics.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FilterField>
        </Box>
      </Collapse>

      {/* ─── column header band ──────────────────────────────────────────── */}
      {studies.length > 0 && (
        <Box
          aria-hidden
          sx={{
            display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', gap: '16px',
            mx: '4px', pl: '22px', pr: '18px', pb: '8px',
          }}
        >
          <HeadCell>Name</HeadCell>
          <HeadCell display={secondaryColDisplay}>Assigned</HeadCell>
          <HeadCell display={dateColDisplay}>Date</HeadCell>
          <HeadCell display={typeColDisplay}>Type</HeadCell>
          <HeadCell display={secondaryColDisplay}>Distribution</HeadCell>
          <HeadCell>Status</HeadCell>
          <Typography
            sx={{
              fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: T.ink3, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            }}
          >
            Actions
          </Typography>
        </Box>
      )}

      {/* ─── study cards ─────────────────────────────────────────────────── */}
      <Box className="csl-list" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: '4px', pb: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {isLoading && !studies.length && (
          <EmptyState>
            <Box component="svg" viewBox="0 0 150 46" sx={ekgSx} aria-hidden>
              <polyline className="ekgline" points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23" />
              <polyline className="ekgpulse" points="0,23 40,23 50,23 56,9 64,37 72,23 92,23 100,15 108,31 116,23 150,23" />
            </Box>
            Loading studies…
          </EmptyState>
        )}
        {!isLoading && !studies.length && (
          <EmptyState>
            <Box sx={{ fontSize: 40 }}>🫀</Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: T.ink2 }}>No studies found</Typography>
            <Typography sx={{ fontSize: 13 }}>{isFiltering ? 'Try clearing filters' : 'New studies will appear here'}</Typography>
          </EmptyState>
        )}

        {studies.map(s => {
          const info = reportInfo[s.studyInstanceUid] || {};
          const status = info.status || s.reportStatus || 'Draft';
          const rtype = info.reportType || s.reportType || '';
          const assignee = info.reportAssignedTo || '';
          const reportId = info.reportId || s.reportId;
          const dist = distributionCounts[s.studyInstanceUid];
          const critical = isCritical(s.studyInstanceUid);
          const research = isResearch(s.studyInstanceUid);
          const af = isAF(s.studyInstanceUid);
          const sColor = statusColor(status);

          return (
            <Card
              key={s.studyInstanceUid}
              sx={{
                position: 'relative',
                flexShrink: 0,
                overflow: 'visible',
                display: 'grid',
                gridTemplateColumns: gridCols,
                alignItems: 'center',
                gap: '16px',
                p: '14px 18px 14px 22px',
                background: 'linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))',
                border: `1px solid ${T.line}`,
                borderRadius: '16px',
                transition: 'transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease',
                '&:hover': { transform: 'translateY(-2px)', borderColor: T.lineStrong, boxShadow: '0 16px 38px -18px rgba(0,0,0,0.85)' },
              }}
            >
              {/* status stripe */}
              <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 14, bottom: 14, width: 3, borderRadius: '0 3px 3px 0', background: sColor }} />

              {/* identity */}
              <Box onClick={() => openStudy(s)} role="button" title="Open study" sx={{ display: 'flex', alignItems: 'center', alignSelf: 'center', gap: '13px', minWidth: 0, cursor: 'pointer' }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 42, height: 42, fontSize: 14, fontWeight: 700, letterSpacing: '0.03em', color: '#04221d',
                    // Critical = red family (was amber-orange, which collided with the draft amber).
                    background: critical ? 'linear-gradient(135deg,#fb7185,#e11d48)' : `linear-gradient(135deg,${T.teal},${T.sky})`,
                    boxShadow: critical ? '0 4px 14px -4px rgba(225,29,72,0.6)' : '0 4px 14px -4px rgba(45,212,191,0.5)',
                  }}
                >
                  {initialsOf(s.patientName)}
                </Avatar>
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: 15, fontWeight: 700, color: '#fff', minWidth: 0 }}>
                    <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prettyName(s.patientName)}</Box>
                    {critical && <Box component="span" title="Critical" sx={{ width: 7, height: 7, borderRadius: '50%', background: T.red, boxShadow: `0 0 8px ${T.red}`, flexShrink: 0 }} />}
                    {af && (
                      <Box
                        component="span"
                        title="Atrial fibrillation"
                        sx={{
                          flexShrink: 0,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: '0.06em',
                          lineHeight: 1,
                          padding: '3px 5px',
                          borderRadius: '4px',
                          color: T.amber,
                          background: 'rgba(247,163,90,0.14)',
                          border: '1px solid rgba(247,163,90,0.45)',
                        }}
                      >
                        AF
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: T.ink3 }}>MRN {s.mrn || '—'}</Typography>
                </Box>
              </Box>

              {/* assigned */}
              <Box sx={{ ...colSx, display: secondaryColDisplay }}>
                <Typography sx={vSx}>{assignee || '—'}</Typography>
              </Box>

              {/* study date/time */}
              <Box sx={{ ...colSx, display: dateColDisplay }}>
                <Typography sx={vSx}>{fmtDate(s.date) || '—'}</Typography>
                <Typography sx={{ fontSize: 12, color: T.ink3 }}>{fmtTime(s.time)}</Typography>
              </Box>

              {/* type — report type when known, else the DICOM modality */}
              <Box sx={{ ...colSx, display: typeColDisplay }}>
                {rtype || s.modalities ? (
                  <Chip
                    label={rtype || (s.modalities || '').split('/')[0]}
                    size="small"
                    sx={{ alignSelf: 'flex-start', fontWeight: 700, fontSize: 12, color: T.violet, background: 'rgba(167,139,250,0.13)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: '7px' }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 12, color: T.ink3 }}>—</Typography>
                )}
              </Box>

              {/* distribution */}
              <Box sx={{ ...colSx, display: secondaryColDisplay }}>
                {dist && dist.count > 0 ? (
                  <Tooltip
                    arrow
                    title={
                      <Box sx={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                        {(dist.distributions || [])
                          .map(
                            (x: any, i: number) =>
                              `${i + 1}. ${x.toProviderName || 'Recipient'} — ${STATUS_LABELS[x.status] || x.status || '—'}` +
                              `${x.createdAt ? ` — ${new Date(x.createdAt).toLocaleDateString()}` : ''}`
                          )
                          .join('\n')}
                      </Box>
                    }
                  >
                    <Chip
                      label={dist.count === 1 ? STATUS_LABELS[dist.distributions?.[0]?.status] || 'Sent' : `${dist.count} sent`}
                      clickable
                      onClick={() => openDistribution(s, info)}
                      size="small"
                      sx={{ alignSelf: 'flex-start', fontWeight: 600, fontSize: 12, color: T.sky, background: 'rgba(56,189,248,0.12)', border: '1px solid rgba(56,189,248,0.34)', borderRadius: '7px', '&:hover': { background: 'rgba(56,189,248,0.2)' } }}
                    />
                  </Tooltip>
                ) : (
                  <Typography sx={{ fontSize: 12, color: T.ink3 }}>None</Typography>
                )}
              </Box>

              {/* status */}
              <Box sx={{ ...colSx, alignItems: 'flex-start' }}>
                <Chip
                  label={status}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: 12, letterSpacing: '0.02em', px: '6px', borderRadius: '999px', color: sColor, background: `${sColor}1f`, border: `1px solid ${sColor}66` }}
                />
              </Box>

              {/* actions */}
              <Stack direction="row" alignItems="center" alignSelf="center" gap={0.75} justifyContent="flex-end">
                {reportId ? (
                  <Button variant="contained" onClick={() => openReport(reportId)} sx={primaryActSx}>View report</Button>
                ) : canGenerate ? (
                  <>
                    <Button variant="contained" endIcon={<ArrowDropDownIcon />} onClick={e => setGenMenu({ anchor: e.currentTarget, study: s })} sx={primaryActSx}>
                      Generate
                    </Button>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12, color: T.ink3 }}>—</Typography>
                )}

                {/* Both entry points are per-clinic features (Super Admin →
                    Feature control). Express lite is never gated — a clinic
                    without the DICOM viewer still reviews clips there. */}
                {dicomViewerEnabled && (
                  <Tooltip title="Open in viewer" arrow>
                    <IconButton onClick={() => openViewer(s.studyInstanceUid)} sx={iconBtnSx}><VisibilityOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                )}
                {measurementEnabled && (
                  <Tooltip title="Measure" arrow>
                    <IconButton onClick={() => openMeasure(s.studyInstanceUid)} sx={{ ...iconBtnSx, '&:hover': { color: T.teal, borderColor: 'rgba(45,212,191,0.5)' } }}><StraightenIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Critical" arrow>
                  <IconButton onClick={() => toggleCritical(s.studyInstanceUid)} sx={{ ...iconBtnSx, ...(critical ? critIconSx : {}) }}>
                    {critical ? <FlagIcon sx={{ fontSize: 18 }} /> : <OutlinedFlagIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={af ? 'Marked as atrial fibrillation' : 'Mark as atrial fibrillation'}
                  arrow
                >
                  <IconButton
                    onClick={() => toggleAF(s.studyInstanceUid)}
                    sx={{
                      ...iconBtnSx,
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                      ...(af ? afIconSx : {}),
                    }}
                  >
                    AF
                  </IconButton>
                </Tooltip>
                <Tooltip title="Research" arrow>
                  <IconButton onClick={() => toggleResearch(s.studyInstanceUid)} sx={{ ...iconBtnSx, ...(research ? { color: T.teal, borderColor: 'rgba(45,212,191,0.5)' } : {}) }}><ScienceOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Attachments" arrow>
                  <IconButton onClick={() => setAttachFor(s.studyInstanceUid)} sx={iconBtnSx}><AttachFileIcon sx={{ fontSize: 18 }} /></IconButton>
                </Tooltip>
              </Stack>
            </Card>
          );
        })}
      </Box>

      {/* generate-template menu (shared, anchored per card) */}
      <Menu
        anchorEl={genMenu?.anchor || null}
        open={!!genMenu}
        onClose={() => setGenMenu(null)}
        PaperProps={{ sx: { background: T.surface2, border: `1px solid ${T.lineStrong}`, minWidth: 200, maxHeight: 280 } }}
      >
        {(templateList?.data || []).map((tpl: any) => (
          <MenuItem
            key={tpl.templateName}
            onClick={() => { if (genMenu) onPickTemplate(genMenu.study, tpl.templateName); setGenMenu(null); }}
            sx={{ fontSize: 13, color: T.ink2, '&:hover': { background: 'rgba(45,212,191,0.12)', color: T.teal } }}
          >
            {tpl.templateName}
          </MenuItem>
        ))}
        {!(templateList?.data || []).length && (
          <MenuItem disabled sx={{ fontSize: 12, color: T.ink3 }}>No templates</MenuItem>
        )}
      </Menu>

      {/* ─── pager ───────────────────────────────────────────────────────── */}
      {studies.length > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ px: '6px', pt: '12px', pb: '4px', borderTop: `1px solid ${T.line}` }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ fontSize: 12.5, color: T.ink3 }}>
            <span>Rows</span>
            <Select size="small" value={resultsPerPage} onChange={e => onResultsPerPageChange(Number(e.target.value))} sx={{ ...fieldInputSx, minWidth: 72 }}>
              {RESULTS_PER_PAGE_OPTIONS.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Button disabled={pageNumber <= 1} onClick={() => onPageNumberChange(pageNumber - 1)} startIcon={<KeyboardArrowLeftIcon />} sx={pagerBtnSx}>Prev</Button>
            <Typography sx={{ fontSize: 13, color: T.ink2, fontVariantNumeric: 'tabular-nums' }}>
              Page {pageNumber}{numOfStudies <= 100 ? ` / ${totalPages}` : ''}
            </Typography>
            <Button disabled={numOfStudies <= 100 && pageNumber >= totalPages} onClick={() => onPageNumberChange(pageNumber + 1)} endIcon={<KeyboardArrowRightIcon />} sx={pagerBtnSx}>Next</Button>
          </Stack>
        </Stack>
      )}

      {/* ─── reused modals ───────────────────────────────────────────────── */}
      <Success handleCloseModal={() => setToast('')} title={toast} isSuccessModalOpen={!!toast} />
      <SelectTopicModal
        isSelectTopicModalOpen={!!topicModalFor}
        studyInstanceUID={topicModalFor || ''}
        onClose={() => setTopicModalFor(null)}
        {...({ setIsAddResearchChecked: () => loadMarkings() } as any)}
      />
      <PatientAttachmentsModal
        isAttachmentsModalOpen={!!attachFor}
        studyInstanceUID={attachFor}
        onClose={() => setAttachFor(null)}
      />
    </Box>
  );
};

// ── small presentational helpers ───────────────────────────────────────────
const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink3 }}>{label}</Typography>
    {children}
  </Box>
);

const EmptyState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', py: '80px', px: '20px', color: T.ink3, textAlign: 'center' }}>
    {children}
  </Box>
);

// ── sx snippets ─────────────────────────────────────────────────────────────
const refLinkSx = {
  fontSize: 13, fontWeight: 600, color: T.ink2, textTransform: 'none',
  px: '14px', py: '9px', borderRadius: '10px', border: `1px solid ${T.line}`,
  background: 'rgba(22,38,59,0.5)', whiteSpace: 'nowrap',
  '&:hover': { color: T.teal, borderColor: 'rgba(45,212,191,0.55)', background: 'rgba(45,212,191,0.08)' },
} as const;

const fieldInputSx = {
  background: T.surface, borderRadius: '10px',
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiInputBase-input': { fontSize: 13, color: T.ink, py: '9px' },
} as const;

const vSx = { fontSize: 13.5, color: T.ink, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

const primaryActSx = {
  fontSize: 13, fontWeight: 700, color: '#04221d', textTransform: 'none', borderRadius: '10px',
  px: '14px', py: '8px', whiteSpace: 'nowrap',
  background: `linear-gradient(92deg, ${T.sky}, ${T.teal})`, boxShadow: '0 6px 16px -7px rgba(45,212,191,0.7)',
  '&:hover': { filter: 'brightness(1.08)', transform: 'translateY(-1px)', background: `linear-gradient(92deg, ${T.sky}, ${T.teal})` },
} as const;

const iconBtnSx = {
  width: 34, height: 34, borderRadius: '10px', border: `1px solid ${T.line}`,
  background: 'rgba(22,38,59,0.5)', color: T.ink2,
  '&:hover': { color: T.ink, borderColor: T.lineStrong, transform: 'translateY(-1px)' },
} as const;

const critIconSx = { color: T.red, borderColor: 'rgba(251,113,133,0.5)', background: 'rgba(251,113,133,0.1)' } as const;
// AF is amber rather than red — it flags a rhythm the reader must account for,
// not an urgent finding. The badge carries the letters "AF", so it does not
// collide with the amber used for draft status the way a bare colour would.
const afIconSx = {
  color: T.amber,
  borderColor: 'rgba(247,163,90,0.5)',
  background: 'rgba(247,163,90,0.12)',
} as const;

const pagerBtnSx = {
  background: T.surface, border: `1px solid ${T.line}`, borderRadius: '10px', color: T.ink,
  fontSize: 13, fontWeight: 600, px: '16px', py: '8px', textTransform: 'none',
  '&:hover': { borderColor: T.teal, color: T.teal, background: T.surface },
  '&.Mui-disabled': { opacity: 0.4, color: T.ink3 },
} as const;

const ekgSx = {
  width: 150, height: 46, mb: '8px', overflow: 'visible',
  '& .ekgline': { fill: 'none', stroke: 'rgba(45,212,191,0.16)', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
  '& .ekgpulse': {
    fill: 'none', stroke: T.teal, strokeWidth: 2.6, strokeLinecap: 'round', strokeLinejoin: 'round',
    filter: 'drop-shadow(0 0 6px rgba(45,212,191,0.9))', strokeDasharray: '48 260',
    animation: 'cslEkg 1.5s linear infinite',
  },
  '@keyframes cslEkg': { from: { strokeDashoffset: 308 }, to: { strokeDashoffset: 0 } },
} as const;

export default CardiacStudyListV2;
